// src/pages/InterviewQuestions.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import interviewData from '../data/interviewquestions.json';
import RandomAd from '../components/RandomAd'; // Importing the RandomAd component

const AdContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 40px 0;
  
  @media (max-width: 768px) {
    margin: 30px 0;
  }
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 50px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 600;
  background: linear-gradient(45deg, #1d1d1f, #424245);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 16px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 21px;
  color: #86868b;
  font-weight: 400;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ControlsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const SearchContainer = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 50px 16px 24px;
  font-size: 17px;
  border: 1px solid #d2d2d7;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0071e3;
    box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.1);
  }
  
  &::placeholder {
    color: #86868b;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #86868b;
  font-size: 20px;
`;

const FilterSelect = styled.select`
  padding: 16px 24px;
  font-size: 17px;
  border: 1px solid #d2d2d7;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0071e3;
    box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.1);
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
`;

const CategoryCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 18px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    background: rgba(255, 255, 255, 0.95);
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const CategoryIcon = styled.span`
  font-size: 32px;
  margin-right: 16px;
`;

const CategoryTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
`;

const QuestionCount = styled.span`
  background: linear-gradient(135deg, #0071e3, #0077ed);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  margin-left: auto;
`;

const QuestionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const QuestionItem = styled.li`
  padding: 16px 0;
  border-bottom: 1px solid #f5f5f7;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:first-child {
    padding-top: 0;
  }
`;

const QuestionText = styled.p`
  color: #1d1d1f;
  font-size: 17px;
  line-height: 1.5;
  margin: 0 0 8px 0;
`;

const QuestionType = styled.span`
  background: ${props => {
    switch(props.type) {
      case 'behavioral': return 'rgba(0, 113, 227, 0.1)';
      case 'technical': return 'rgba(52, 199, 89, 0.1)';
      case 'situational': return 'rgba(255, 159, 10, 0.1)';
      default: return 'rgba(134, 134, 139, 0.1)';
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'behavioral': return '#0071e3';
      case 'technical': return '#30d158';
      case 'situational': return '#ff9f0a';
      default: return '#86868b';
    }
  }};
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
`;

const ResultsCount = styled.div`
  text-align: center;
  color: #86868b;
  font-size: 17px;
  margin-bottom: 30px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #86868b;
  font-size: 18px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 18px;
  border: 1px dashed #e8e8ed;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const InterviewQuestions = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    filterQuestions();
  }, [selectedCategory, searchQuery]);

  const filterQuestions = () => {
    let filtered = interviewData.categories;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(cat => cat.id === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.map(cat => ({
        ...cat,
        questions: cat.questions.filter(q => 
          q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (q.tips && q.tips.some(tip => tip.toLowerCase().includes(searchQuery.toLowerCase())))
        )
      })).filter(cat => cat.questions.length > 0);
    }

    setFilteredData(filtered);
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId === selectedCategory ? 'all' : categoryId);
  };

  const getTotalQuestions = () => {
    return filteredData.reduce((total, category) => total + category.questions.length, 0);
  };

  const getCategoryIcon = (categoryId) => {
    const icons = {
      behavioral: '💭',
      technical: '⚡',
      safety: '🛡️',
      situational: '🎯',
      leadership: '👥',
      industry: '🏭'
    };
    return icons[categoryId] || '❓';
  };

  return (
    <PageContainer>
      <HeaderSection>
        <PageTitle>Interview Question Bank</PageTitle>
        <PageSubtitle>
          Prepare for your power utility industry interviews with our comprehensive collection 
          of behavioral, technical, and situational questions.
        </PageSubtitle>
      </HeaderSection>
      {/* Strategic Ad Placement 1: After Header Section */}
      {/* This captures users when they're most engaged after understanding the page purpose */}
      <AdContainer>
        <RandomAd />
      </AdContainer>

      <ControlsSection>
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search questions, topics, or keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <SearchIcon>🔍</SearchIcon>
        </SearchContainer>
        
        <FilterSelect 
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="all">All Categories</option>
          {interviewData.categories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </FilterSelect>
      </ControlsSection>

      <ResultsCount>
        Showing {getTotalQuestions()} question{getTotalQuestions() !== 1 ? 's' : ''}
        {selectedCategory !== 'all' && ` in ${interviewData.categories.find(cat => cat.id === selectedCategory)?.name}`}
        {searchQuery && ` matching "${searchQuery}"`}
      </ResultsCount>

      {filteredData.length > 0 ? (
        <CategoriesGrid>
          {filteredData.map(category => (
            <CategoryCard 
              key={category.id}
              onClick={() => handleCategorySelect(category.id)}
            >
              <CategoryHeader>
                <CategoryIcon>{getCategoryIcon(category.id)}</CategoryIcon>
                <CategoryTitle>{category.name}</CategoryTitle>
                <QuestionCount>{category.questions.length}</QuestionCount>
              </CategoryHeader>
              
              <QuestionsList>
                {category.questions.map((question, index) => (
                  <QuestionItem key={index}>
                    <QuestionText>{question.question}</QuestionText>
                    <QuestionType type={question.type}>
                      {question.type.charAt(0).toUpperCase() + question.type.slice(1)}
                    </QuestionType>
                  </QuestionItem>
                ))}
              </QuestionsList>
            </CategoryCard>
          ))}
        </CategoriesGrid>
        
      ) : (
        <EmptyState>
          No questions found matching your criteria. Try adjusting your search or selecting a different category.
        </EmptyState>
      )}
    </PageContainer>
  );
};

export default InterviewQuestions;